import {
  BuildingItemRequirement,
  ItemType,
  Recipe,
  RecipeIngredient,
  RECIPES,
  RECIPES_BY_ID,
  ITEM_TYPES,
  createEmptyItemCounts,
} from './constants';
import { secondsToTicks } from './utils/TickIntervals';

export interface HandCraftingQueueEntry {
  readonly id: string;
  readonly recipeId: Recipe['id'];
  readonly recipeName: string;
  readonly totalCount: number;
  readonly remainingCount: number;
  readonly ticksPerCraft: number;
  readonly progressTicks: number;
}

export const HAND_CRAFTING_SPEED_MULTIPLIER = 4;

export const getHandCraftingTicksPerCraft = (recipe: Recipe): number => {
  return Math.max(
    1,
    Math.floor(
      secondsToTicks(recipe.craftingTime) / HAND_CRAFTING_SPEED_MULTIPLIER
    )
  );
};

type ItemCounts = Record<ItemType, number>;

type PlannerCraftCounts = Record<Recipe['id'], number>;

type PlannerConsumption = Partial<ItemCounts>;

interface PlannerContext {
  simulatedInventory: ItemCounts;
  newlyProduced: ItemCounts;
  consumptionFromInventory: PlannerConsumption;
  plannedCraftCounts: PlannerCraftCounts;
}

interface HandCraftingPlanResult {
  craftCounts: PlannerCraftCounts;
  consumption: PlannerConsumption;
}

const recipeByOutput = new Map<ItemType, Recipe>();

Object.values(RECIPES).forEach(recipe => {
  recipe.outputs.forEach((output: RecipeIngredient) => {
    recipeByOutput.set(output.item, recipe);
  });
});

const getRecipeForOutput = (item: ItemType): Recipe | undefined => {
  return recipeByOutput.get(item);
};

const getOutputPerCraft = (recipe: Recipe, item: ItemType): number => {
  const output = recipe.outputs.find(entry => entry.item === item);
  return output ? output.count : 0;
};

const consumeFromSimulatedInventory = (
  item: ItemType,
  amount: number,
  context: PlannerContext,
  trackConsumption: boolean
): boolean => {
  if (amount <= 0) {
    return true;
  }

  const available = context.simulatedInventory[item] ?? 0;
  if (available < amount) {
    return false;
  }

  context.simulatedInventory[item] = available - amount;

  const produced = context.newlyProduced[item] ?? 0;
  if (produced > 0) {
    const fromProduced = Math.min(produced, amount);
    context.newlyProduced[item] = produced - fromProduced;
    amount -= fromProduced;
  }

  if (trackConsumption && amount > 0) {
    const current = context.consumptionFromInventory[item] ?? 0;
    context.consumptionFromInventory[item] = current + amount;
  }

  return true;
};

const addPlannedCraft = (recipe: Recipe, context: PlannerContext): void => {
  const current = context.plannedCraftCounts[recipe.id] ?? 0;
  context.plannedCraftCounts[recipe.id] = current + 1;
};

const scheduleCraft = (
  item: ItemType,
  amountNeeded: number,
  context: PlannerContext
): boolean => {
  if (amountNeeded <= 0) {
    return true;
  }

  const recipe = getRecipeForOutput(item);
  if (!recipe) {
    return false;
  }

  const outputPerCraft = getOutputPerCraft(recipe, item);
  if (outputPerCraft <= 0) {
    return false;
  }

  const craftsNeeded = Math.ceil(amountNeeded / outputPerCraft);

  for (let craft = 0; craft < craftsNeeded; craft++) {
    for (const input of recipe.inputs) {
      if (!consumeForCraft(input.item, input.count, context)) {
        return false;
      }
    }

    addPlannedCraft(recipe, context);

    recipe.outputs.forEach(output => {
      const existing = context.simulatedInventory[output.item] ?? 0;
      context.simulatedInventory[output.item] = existing + output.count;

      const produced = context.newlyProduced[output.item] ?? 0;
      context.newlyProduced[output.item] = produced + output.count;
    });
  }

  return true;
};

const consumeForCraft = (
  item: ItemType,
  amount: number,
  context: PlannerContext
): boolean => {
  if (amount <= 0) {
    return true;
  }

  let available = context.simulatedInventory[item] ?? 0;

  if (available < amount) {
    const missing = amount - available;
    if (!scheduleCraft(item, missing, context)) {
      return false;
    }
    available = context.simulatedInventory[item] ?? 0;
  }

  return consumeFromSimulatedInventory(item, amount, context, true);
};

const ensureRequirement = (
  item: ItemType,
  amount: number,
  context: PlannerContext
): boolean => {
  if (amount <= 0) {
    return true;
  }

  let available = context.simulatedInventory[item] ?? 0;
  if (available < amount) {
    const missing = amount - available;
    if (!scheduleCraft(item, missing, context)) {
      return false;
    }
    available = context.simulatedInventory[item] ?? 0;
    if (available < amount) {
      return false;
    }
  }

  return consumeFromSimulatedInventory(item, amount, context, false);
};

export const calculateHandCraftingQueuedOutputs = (
  queue: readonly HandCraftingQueueEntry[]
): ItemCounts => {
  const totals = createEmptyItemCounts();

  queue.forEach(entry => {
    const recipe = RECIPES_BY_ID[entry.recipeId];
    if (!recipe) {
      return;
    }

    recipe.outputs.forEach(output => {
      totals[output.item] += output.count * entry.remainingCount;
    });
  });

  return totals;
};

export const calculateHandCraftingPostQueueInventory = (
  currentInventory: Record<ItemType, number>,
  queue: readonly HandCraftingQueueEntry[]
): ItemCounts => {
  const totals = createEmptyItemCounts();

  ITEM_TYPES.forEach(itemType => {
    totals[itemType] = currentInventory[itemType] ?? 0;
  });

  queue.forEach(entry => {
    const recipe = RECIPES_BY_ID[entry.recipeId];
    if (!recipe) {
      return;
    }

    const craftsRemaining = Math.max(0, entry.remainingCount);
    if (craftsRemaining === 0) {
      return;
    }

    recipe.outputs.forEach(output => {
      if (output.count <= 0) {
        return;
      }

      const totalOutput = output.count * craftsRemaining;
      if (totalOutput <= 0) {
        return;
      }

      totals[output.item] += totalOutput;
    });
  });

  return totals;
};

export const planHandCraftingForRequirements = (
  requirements: readonly BuildingItemRequirement[],
  playerInventoryCounts: ItemCounts,
  queuedOutputs: ItemCounts
): HandCraftingPlanResult | null => {
  if (requirements.length === 0) {
    return { craftCounts: {}, consumption: {} };
  }

  const context: PlannerContext = {
    simulatedInventory: createEmptyItemCounts(),
    newlyProduced: createEmptyItemCounts(),
    consumptionFromInventory: {},
    plannedCraftCounts: {},
  };

  ITEM_TYPES.forEach(itemType => {
    const inventoryCount = playerInventoryCounts[itemType] ?? 0;
    const queuedCount = queuedOutputs[itemType] ?? 0;
    context.simulatedInventory[itemType] = inventoryCount + queuedCount;
    context.newlyProduced[itemType] = queuedCount;
  });

  for (const requirement of requirements) {
    if (!ensureRequirement(requirement.item, requirement.amount, context)) {
      return null;
    }
  }

  return {
    craftCounts: context.plannedCraftCounts,
    consumption: context.consumptionFromInventory,
  };
};

export const normalizeHandCraftingQueue = (
  queue: readonly HandCraftingQueueEntry[]
): HandCraftingQueueEntry[] => {
  return queue
    .map(entry => {
      const recipe = RECIPES_BY_ID[entry.recipeId];
      if (!recipe) {
        return null;
      }

      const ticksPerCraft = getHandCraftingTicksPerCraft(recipe);

      let remainingCount = Math.max(0, entry.remainingCount);
      let progressTicks = Math.max(0, entry.progressTicks);

      if (ticksPerCraft > 0 && progressTicks >= ticksPerCraft) {
        const completedCrafts = Math.floor(progressTicks / ticksPerCraft);
        const craftsCompleted = Math.min(completedCrafts, remainingCount);
        progressTicks -= craftsCompleted * ticksPerCraft;
        remainingCount -= craftsCompleted;
      }

      const totalCount = Math.max(entry.totalCount, remainingCount);

      if (remainingCount <= 0) {
        return null;
      }

      return {
        id: entry.id,
        recipeId: entry.recipeId,
        recipeName: recipe.name,
        totalCount,
        remainingCount,
        ticksPerCraft,
        progressTicks,
      };
    })
    .filter((entry): entry is HandCraftingQueueEntry => entry !== null);
};

export type HandCraftingPlan = HandCraftingPlanResult;
